import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, TextField, MenuItem } from '@mui/material'
import { Add, Rule, Description } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listInspections, type QualityInspectionListItem, type QcCheckpoint, type QcDecision } from '../../api/productieCalitate'
import InspectionFormDialog from './InspectionFormDialog'
import NonconformityTypesDialog from './NonconformityTypesDialog'
import QualityCertificateDialog from './QualityCertificateDialog'

const DECISION_COLOR: Record<QcDecision, 'success' | 'error' | 'warning' | 'info'> = {
  Acceptat: 'success', Respins: 'error', Rework: 'warning', Carantina: 'info',
}
const CHECKPOINT_LABEL: Record<QcCheckpoint, string> = { ControlProces: 'Control în proces', ControlFinal: 'Control final' }

export default function CalitatePage() {
  const [checkpointFilter, setCheckpointFilter] = useState<QcCheckpoint | ''>('')
  const [lotFilter, setLotFilter] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [certificateTarget, setCertificateTarget] = useState<{ articleId: string; lotNumber: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['productie', 'calitate', 'inspectii', checkpointFilter, lotFilter],
    queryFn: () => listInspections({ checkpoint: checkpointFilter || undefined, lotNumber: lotFilter || undefined }),
  })

  const columns = useMemo<GridColDef<QualityInspectionListItem>[]>(() => [
    { field: 'inspectedAt', headerName: 'Data', width: 160, valueFormatter: (v: string) => new Date(v).toLocaleString('ro-RO') },
    { field: 'checkpoint', headerName: 'Punct', width: 150, valueGetter: (v: QcCheckpoint) => CHECKPOINT_LABEL[v] },
    { field: 'articleDescription', headerName: 'Articol', flex: 1, minWidth: 200, renderCell: (p) => `${p.row.articleCode} — ${p.row.articleDescription}` },
    { field: 'lotNumber', headerName: 'Lot', width: 140, renderCell: (p) => p.row.lotNumber ? <code>{p.row.lotNumber}</code> : '—' },
    { field: 'productionOrderNumber', headerName: 'OP', width: 130, valueGetter: (v: string | null) => v ?? '—' },
    { field: 'quantityThousands', headerName: 'Cant. (mii)', width: 110, type: 'number' },
    { field: 'decision', headerName: 'Decizie', width: 130, sortable: false, renderCell: (p) => <Chip size="small" label={p.row.decision} color={DECISION_COLOR[p.row.decision]} variant="outlined" /> },
    { field: 'nonconformityCount', headerName: 'Neconf.', width: 90, type: 'number' },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => p.row.lotNumber ? (
        <IconButtonCertificate onClick={() => setCertificateTarget({ articleId: p.row.articleId, lotNumber: p.row.lotNumber! })} />
      ) : null,
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Producție</Typography>
        <Typography variant="inherit" color="text.primary">Control calitate</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Control calitate</Typography>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={2}>
          <TextField select label="Punct de control" value={checkpointFilter} onChange={(e) => setCheckpointFilter(e.target.value as QcCheckpoint | '')} sx={{ width: 200 }}>
            <MenuItem value="">Toate</MenuItem>
            <MenuItem value="ControlProces">Control în proces</MenuItem>
            <MenuItem value="ControlFinal">Control final</MenuItem>
          </TextField>
          <TextField label="Lot" value={lotFilter} onChange={(e) => setLotFilter(e.target.value)} sx={{ width: 180 }} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Rule />} onClick={() => setCatalogOpen(true)}>Catalog neconformități</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>Inspecție nouă</Button>
        </Stack>
      </Stack>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <InspectionFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <NonconformityTypesDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} />
      <QualityCertificateDialog target={certificateTarget} onClose={() => setCertificateTarget(null)} />
    </Stack>
  )
}

function IconButtonCertificate({ onClick }: { onClick: () => void }) {
  return (
    <Button size="small" onClick={onClick} title="Certificat de calitate" sx={{ minWidth: 0, p: 0.5 }}>
      <Description fontSize="small" />
    </Button>
  )
}
